import dayjs, { Dayjs } from 'dayjs';
import { StageOption } from '../components/AdminPanel/StagesTable';
import { STAGE_0, STAGE_1, STAGE_2, STAGE_3, STAGE_4, STAGE_5 } from '../utils/stages';
import { romanize } from '../utils/string-tools';

class Olympics {
	id: number;
	edition: string;
	schoolYear: string;
	teachersRegistrationFinishDate: Dayjs | null;
	studentsRegistrationFinishDate: Dayjs | null;
	registrationConfirmationFinishDate: Dayjs | null;
	firstStagePublished: boolean;
	secondAStagePublished: boolean;
	secondBStagePublished: boolean;
	secondBStageQualificationPublished: boolean;
	secondBOralDescriptionPublished: boolean;
	thirdAStagePublished: boolean;
	thirdBStagePublished: boolean;
	thirdStageQuestionnaireEnabled: boolean;
	isStudentRegisterEnabled: boolean;
	firstStageMaxPoints: number;

	secondStagePoints: {
		partA: {
			v1: {
				part1: number;
				part2: number;
			};
			v2: {
				part1: number;
				part2: number;
			};
			total: number;
			max: {
				v1: {
					part1: number;
					part2: number;
				};
				v2: {
					part1: number;
					part2: number;
				};
			};
		};
		partB: {
			part1: number;
			part2: number;
			part3: number;
			total: number;
			totalStudentsLimit: number;
			stageClosedDate: Dayjs | null;
			stageClosed: boolean;
			max: {
				part1: number;
				part2: number;
				part3: number;
			};
		};
	};
	thirdStagePoints: {
		partA: {
			points: number;
			finalistPoints: number;
			maxPoints: number;
		};
		partB: {
			laureate1: number;
			laureate2: number;
			laureate3: number;
			max: {
				part1: number;
				part2: number;
			};
		};
	};
	/** Okręg Zagranica: Finalista wymaga sumy ustnych IIIB ≥ progu (gdy włączone). */
	foreignFinalistRequiresIIIb: boolean;
	/** Minimalna suma punktów IIIB (I+II) dla Finalisty — tylko Zagranica, gdy foreignFinalistRequiresIIIb. */
	foreignFinalistIIIbMinPoints: number;
	helpIntro?: string;
	helpLinks?: { label: string; url: string; roles: string[] }[];

	constructor(
		edition: string,
		schoolYear: string,
		teachersRegistrationFinishDate: dayjs.Dayjs | null,
		studentsRegistrationFinishDate: dayjs.Dayjs | null,
		registrationConfirmationFinishDate: dayjs.Dayjs | null,
		firstStagePublished: boolean,
		secondAStagePublished: boolean,
		secondBStagePublished: boolean,
		secondBStageQualificationPublished: boolean,
		secondBOralDescriptionPublished: boolean,
		thirdAStagePublished: boolean,
		thirdBStagePublished: boolean,
		thirdStageQuestionnaireEnabled: boolean,
		isStudentRegisterEnabled: boolean,
		firstStageMaxPoints: number,
		secondStagePoints: {
			partA: {
				v1: { part1: number; part2: number };
				v2: { part1: number; part2: number };
				total: number;
				max: { v1: { part1: number; part2: number }; v2: { part1: number; part2: number } };
			};
			partB: {
				part1: number;
				part2: number;
				part3: number;
				total: number;
				totalStudentsLimit: number;
				stageClosedDate: dayjs.Dayjs | null;
				stageClosed: boolean;
				max: { part1: number; part2: number; part3: number };
			};
		},
		thirdStagePoints: {
			partA: { points: number; finalistPoints: number; maxPoints: number };
			partB: { laureate1: number; laureate2: number; laureate3: number; max: { part1: number; part2: number } };
		},
	) {
		this.id = 0;
		this.edition = edition;
		this.schoolYear = schoolYear;
		this.teachersRegistrationFinishDate = teachersRegistrationFinishDate;
		this.studentsRegistrationFinishDate = studentsRegistrationFinishDate;
		this.registrationConfirmationFinishDate = registrationConfirmationFinishDate;
		this.firstStagePublished = firstStagePublished;
		this.secondAStagePublished = secondAStagePublished;
		this.secondBStagePublished = secondBStagePublished;
		this.secondBStageQualificationPublished = secondBStageQualificationPublished;
		this.secondBOralDescriptionPublished = secondBOralDescriptionPublished;
		this.thirdAStagePublished = thirdAStagePublished;
		this.thirdBStagePublished = thirdBStagePublished;
		this.thirdStageQuestionnaireEnabled = thirdStageQuestionnaireEnabled;
		this.secondStagePoints = secondStagePoints;
		this.thirdStagePoints = thirdStagePoints;
		this.isStudentRegisterEnabled = isStudentRegisterEnabled;
		this.firstStageMaxPoints = firstStageMaxPoints;
		this.foreignFinalistRequiresIIIb = false;
		this.foreignFinalistIIIbMinPoints = 20;
	}

	public static fromApiResponse(data: any) {
		const inst = new Olympics(
			romanize(data['edition']),
			data['school_year'],
			data['teacher_registration_finish_date'] ? dayjs(data['teacher_registration_finish_date']) : null,
			data['students_registration_finish_date'] ? dayjs(data['students_registration_finish_date']) : null,
			data['registration_confirmation_finish_date'] ? dayjs(data['registration_confirmation_finish_date']) : null,
			data['first_stage_published'] === 1,
			data['second_a_stage_published'] === 1,
			data['second_b_stage_published'] === 1,
			data['second_b_stage_qualification_published'] === 1,
			data['second_b_oral_description_published'] === 1,
			data['third_a_stage_published'] === 1,
			data['third_b_stage_published'] === 1,
			data['third_stage_questionnaire_enabled'] === 1,
			data['is_student_register_enabled'] === 1,
			data['first_stage_max_points'] ? data['first_stage_max_points'] : 0,
			{
				partA: {
					v1: {
						part1: data['second_a_one_stage_points'] ? data['second_a_one_stage_points'] : 0,
						part2: data['second_a_two_stage_points'] ? data['second_a_two_stage_points'] : 0,
					},
					v2: {
						part1: data['second_a_one_alt_stage_points'] ? data['second_a_one_alt_stage_points'] : 0,
						part2: data['second_a_two_alt_stage_points'] ? data['second_a_two_alt_stage_points'] : 0,
					},
					total: data['second_a_total_stage_points'] ? data['second_a_total_stage_points'] : 0,
					max: {
						v1: {
							part1: data['second_a_one_stage_max_points'] ? data['second_a_one_stage_max_points'] : 0,
							part2: data['second_a_two_stage_max_points'] ? data['second_a_two_stage_max_points'] : 0,
						},
						v2: {
							part1: data['second_a_one_alt_stage_max_points'] ? data['second_a_one_alt_stage_max_points'] : 0,
							part2: data['second_a_two_alt_stage_max_points'] ? data['second_a_two_alt_stage_max_points'] : 0,
						},
					},
				},
				partB: {
					part1: data['second_b_one_stage_points'] ? data['second_b_one_stage_points'] : 0,
					part2: data['second_b_two_stage_points'] ? data['second_b_two_stage_points'] : 0,
					part3: data['second_b_three_stage_points'] ? data['second_b_three_stage_points'] : 0,
					total: data['second_b_total_stage_points'] ? data['second_b_total_stage_points'] : 0,
					totalStudentsLimit: data['second_b_total_students_limit'] ? data['second_b_total_students_limit'] : 0,
					stageClosedDate: data['second_b_end_date'] ? dayjs(data['second_b_end_date']) : null,
					stageClosed: data['second_b_end_date'] ? Olympics.isStageClosed(data['second_b_end_date']) : true,
					max: {
						part1: data['second_b_one_stage_max_points'] ? data['second_b_one_stage_max_points'] : 0,
						part2: data['second_b_two_stage_max_points'] ? data['second_b_two_stage_max_points'] : 0,
						part3: data['second_b_three_stage_max_points'] ? data['second_b_three_stage_max_points'] : 0,
					},
				},
			},
			{
				partA: {
					points: data['third_a_stage_points'] ? data['third_a_stage_points'] : 0,
					finalistPoints: data['finalists_points'] ? data['finalists_points'] : 0,
					maxPoints: data['third_a_stage_max_points'] ? data['third_a_stage_max_points'] : 0,
				},
				partB: {
					laureate1: data['laureate_one_points'] ? data['laureate_one_points'] : 0,
					laureate2: data['laureate_two_points'] ? data['laureate_two_points'] : 0,
					laureate3: data['laureate_three_points'] ? data['laureate_three_points'] : 0,
					max: {
						part1: data['third_b_one_stage_max_points'] ? data['third_b_one_stage_max_points'] : 0,
						part2: data['third_b_two_stage_max_points'] ? data['third_b_two_stage_max_points'] : 0,
					},
				},
			},
		);
		inst.foreignFinalistRequiresIIIb =
			data['foreign_finalist_requires_iiib'] === 1 || data['foreign_finalist_requires_iiib'] === true;
		inst.foreignFinalistIIIbMinPoints =
			data['foreign_finalist_iiib_min_points'] !== undefined && data['foreign_finalist_iiib_min_points'] !== null
				? Number(data['foreign_finalist_iiib_min_points'])
				: 20;
		inst.id = Number(data['id']) || 0;
		inst.helpIntro = data['helpIntro'] ?? data['help_intro'] ?? '';
		inst.helpLinks = Array.isArray(data['helpLinks'])
			? data['helpLinks']
			: Array.isArray(data['help_links'])
				? data['help_links']
				: [];
		return inst;
	}

	public getPublishedStage = (): StageOption => {
		if (this.thirdBStagePublished) {
			return STAGE_5;
		}
		if (this.thirdAStagePublished) {
			return STAGE_4;
		}
		if (this.secondBStagePublished) {
			return STAGE_3;
		}
		if (this.secondAStagePublished) {
			return STAGE_2;
		}
		if (this.firstStagePublished) {
			return STAGE_1;
		}
		return STAGE_0;
	};

	private static isStageClosed = (endTimestamp: string): boolean => {
		const timestamp = new Date(endTimestamp).valueOf();
		if (isNaN(timestamp.valueOf())) {
			return true;
		} else {
			return Date.now() > timestamp;
		}
	};
}

export default Olympics;
